import { Outlet } from "react-router-dom";
import { Box, useMediaQuery } from "@mui/material";
import { useState } from "react";

import { AuthGuard } from "../guards/AuthGuard";
import SideNav from "../componenets/nav/SideNav";
import TopNav from "../componenets/TopNav";
import { MenuProvider } from "../contexts/MenuContext";
import { CompanyProvider } from "../contexts/CompanyContext";
import { AdminMessageProvider } from "../contexts/AdminMessageContext";
import AdminChatbot from "../componenets/chat/AdminChatbot";
import NewMessageDialog from "../componenets/messages/NewMessageDialog";
import { layout } from "../theme";

const DashboardLayout = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  // Below md the SideNav becomes a temporary drawer overlay; the main content
  // should reclaim the space it used to reserve for the permanent sidebar.
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const gutter = isSmallScreen ? layout.gutterMobile : layout.gutter;

  const changeIsMenuOpen = () => setIsMenuOpen((prev) => !prev);

  return (
    <AuthGuard>
      <CompanyProvider>
        <AdminMessageProvider>
          <MenuProvider>
            <Box
              sx={{
                display: "flex",
                // `dvh`, not `vh`: on mobile Safari `100vh` is the height the viewport has with
                // the URL bar RETRACTED, so a fixed shell sized to it runs under the browser
                // chrome and the bottom of every page is unreachable. `dvh` tracks the bar.
                height: "100dvh",
                overflow: "hidden",
                bgcolor: "background.default",
              }}
            >
              <TopNav
                changeIsMenuOpen={changeIsMenuOpen}
                onChatOpen={() => setIsChatOpen(true)}
              />
              <SideNav
                isMobileOpen={isMenuOpen}
                onMobileClose={changeIsMenuOpen}
              />
              {/*
                Content area is fixed-positioned below the topbar and beside
                the sidebar. We use plain HTML+inline style instead of MUI sx
                so the RTL stylis plugin doesn't flip our `left`/`right`
                values — in RTL the sidebar lives on the visual right, and we
                want `right: 190px` to mean exactly that, physically.
              */}
              <main
                style={{
                  position: "fixed",
                  // Symmetric gutter on all four sides baked into <main>'s
                  // dimensions. We use a plain <main> element with inline
                  // styles so neither MUI sx nor the RTL stylis plugin can
                  // touch these positioning values.
                  top: `${layout.envBannerHeight + layout.topBarHeight + gutter}px`,
                  bottom: `${gutter}px`,
                  left: `${gutter}px`,
                  // Below md the SideNav is a temporary drawer overlay — claim
                  // the full width back. Above md, reserve the sidebar's slot
                  // plus +16px so the visible right gap matches the left.
                  right: isSmallScreen
                    ? `${gutter}px`
                    : `${layout.sideNavWidth + layout.gutter + 16}px`,
                  overflowY: "auto",
                  overflowX: "hidden",
                  zIndex: 1,
                  backgroundColor: "var(--mui-palette-background-default, #F4F6FA)",
                }}
              >
                <div style={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
                  <Outlet />
                </div>
              </main>
            </Box>
            <NewMessageDialog />
          </MenuProvider>
          {/* The company-less-persona gate lives INSIDE AdminChatbot, not here: this layout is
              rendered by a route that useRoutes() resolves above <AuthProvider> in App, so
              useAuth() at this level is undefined and destructuring it throws. */}
          <AdminChatbot
            isOpen={isChatOpen}
            onClose={() => setIsChatOpen(false)}
          />
        </AdminMessageProvider>
      </CompanyProvider>
    </AuthGuard>
  );
};

export default DashboardLayout;
